import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    dismissPwaInstallPrompt,
    getPwaInstallGuide,
    getPwaInstallState,
    requestPwaInstall,
    subscribeToPwaInstallState,
} from '../utils/pwaInstall';

// CarFido brand palette (#D81F26 red, #414141 grey, #707070 grey-75, #F9F9F9 light grey).
// grey-75 on white is 4.95:1 and white on red 5.07:1, so both pass WCAG AA for text.
const BTN_PRIMARY = 'rounded-xl bg-[#D81F26] text-sm font-black text-white transition hover:bg-[#D81F26]/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#414141] disabled:bg-[#707070]';

const ShareIcon = () => (
    <svg aria-hidden="true" className="inline h-4 w-4 -translate-y-0.5 text-[#414141]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 15V3m0 0L8 7m4-4 4 4" /><path d="M5 11v9h14v-9" />
    </svg>
);

const Strong = ({ children }) => <span className="font-black text-[#414141]">{children}</span>;

const InstallIllustration = ({ ios = false }) => (
    <div aria-hidden="true" className="relative flex h-20 w-24 shrink-0 items-center justify-center rounded-2xl bg-[#f6f6f6]">
        <div className="relative h-16 w-10 rounded-[10px] border-[3px] border-[#414141] bg-white shadow-sm">
            <img src="/android-chrome-192x192.png" alt="" className="mx-auto mt-2 h-7 w-7 rounded-lg" />
            <div className="absolute bottom-1.5 left-1/2 h-1 w-4 -translate-x-1/2 rounded-full bg-[#707070]/40" />
        </div>
        {ios ? (
            <svg className="absolute right-1 top-1 text-[#D81F26]" width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V3m0 0L8 7m4-4 4 4" /><path d="M5 11v8h14v-8" />
            </svg>
        ) : (
            <svg className="absolute right-1 top-1 text-[#D81F26]" width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v13m0 0-4-4m4 4 4-4" /><path d="M5 20h14" />
            </svg>
        )}
    </div>
);

const Steps = ({ ios = false, items = [] }) => (
    <div className="flex items-start gap-4 rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-4">
        <InstallIllustration ios={ios} />
        <ol className="min-w-0 space-y-2 text-sm font-semibold leading-snug text-[#484848]">
            {items.map((item, index) => (
                <li key={index} className="flex gap-2">
                    <span className="font-black text-[#414141]">{index + 1}.</span>
                    <span>{item}</span>
                </li>
            ))}
        </ol>
    </div>
);

export const IosInstallSteps = () => (
    <Steps
        ios
        items={[
            <>Tap Safari's <Strong>Share</Strong> button <ShareIcon />.</>,
            <>Scroll and tap <Strong>Add to Home Screen</Strong>.</>,
            <>Tap <Strong>Add</Strong>.</>,
        ]}
    />
);

const CopyLink = () => {
    const url = typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://www.carfido.com/';
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch (_) {
            setCopied(false); // Clipboard can be blocked in in-app browsers; the URL stays visible to copy by hand.
        }
    };
    return (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#e5e5e5] bg-white px-3 py-2">
            <span className="min-w-0 truncate text-sm font-bold text-[#414141]">{url}</span>
            <button type="button" onClick={copy} className="shrink-0 rounded-lg px-2 py-1 text-xs font-black text-[#D81F26] hover:bg-[#F9F9F9]">
                {copied ? 'Copied' : 'Copy link'}
            </button>
        </div>
    );
};

const GUIDE_CONTENT = {
    'ios-safari': {
        title: 'Add CarFido to your Home Screen',
        body: () => (
            <>
                <IosInstallSteps />
                <p className="mt-3 text-xs font-semibold leading-relaxed text-[#707070]">CarFido then opens from its own icon without Safari's address bar. The first time you open it, sign in with your usual phone number or email.</p>
            </>
        ),
    },
    'ios-other-browser': {
        title: 'Add CarFido to your Home Screen',
        body: () => (
            <>
                <Steps
                    ios
                    items={[
                        <>Tap the <Strong>Share</Strong> button <ShareIcon /> in the address bar. In Edge or Firefox, open the <Strong>menu</Strong> first and choose <Strong>Share</Strong>.</>,
                        <>Tap <Strong>Add to Home Screen</Strong>.</>,
                        <>Tap <Strong>Add</Strong>.</>,
                    ]}
                />
                <p className="mt-3 text-xs font-semibold leading-relaxed text-[#707070]">Needs iOS 16.4 or later. If you don't see Add to Home Screen, open this page in Safari instead.</p>
                <CopyLink />
            </>
        ),
    },
    'ios-in-app': {
        title: 'Open CarFido in Safari',
        body: () => (
            <>
                <p className="mb-3 text-sm font-semibold leading-relaxed text-[#707070]">This app's built-in browser can't add CarFido to your Home Screen. Apple only allows it from Safari.</p>
                <Steps
                    ios
                    items={[
                        <>Tap <Strong>•••</Strong> or the <Strong>Share</Strong> icon in this app and choose <Strong>Open in Safari</Strong> (sometimes called <Strong>Open in browser</Strong>).</>,
                        <>In Safari, go to <Strong>Settings</Strong> and tap <Strong>Add CarFido to Home Screen</Strong>.</>,
                    ]}
                />
                <p className="mt-3 text-xs font-semibold text-[#707070]">Or copy the link and paste it into Safari:</p>
                <CopyLink />
            </>
        ),
    },
    'android-manual': {
        title: 'Install CarFido',
        body: () => (
            <>
                <Steps
                    items={[
                        <>Tap the browser menu <Strong>⋮</Strong> at the top right.</>,
                        <>Tap <Strong>Install app</Strong> or <Strong>Add to Home screen</Strong>.</>,
                        <>Tap <Strong>Install</Strong> to confirm.</>,
                    ]}
                />
                <p className="mt-3 text-xs font-semibold leading-relaxed text-[#707070]">Don't see the option? Open this page in Chrome and try again.</p>
            </>
        ),
    },
    'android-samsung': {
        title: 'Install CarFido',
        body: () => (
            <Steps
                items={[
                    <>Tap the menu <Strong>☰</Strong> at the bottom of the screen.</>,
                    <>Tap <Strong>Add page to</Strong>, then <Strong>Home screen</Strong>.</>,
                    <>Tap <Strong>Add</Strong> to confirm.</>,
                ]}
            />
        ),
    },
    'android-in-app': {
        title: 'Open CarFido in Chrome',
        body: () => (
            <>
                <p className="mb-3 text-sm font-semibold leading-relaxed text-[#707070]">This app's built-in browser can't install CarFido.</p>
                <Steps
                    items={[
                        <>Tap <Strong>⋮</Strong> in this app and choose <Strong>Open in Chrome</Strong> (or <Strong>Open in browser</Strong>).</>,
                        <>In Chrome, go to <Strong>Settings</Strong> and tap <Strong>Install CarFido</Strong>.</>,
                    ]}
                />
                <p className="mt-3 text-xs font-semibold text-[#707070]">Or copy the link and paste it into Chrome:</p>
                <CopyLink />
            </>
        ),
    },
};

const InstallDialog = ({ guide, onClose }) => {
    const closeRef = useRef(null);
    const content = GUIDE_CONTENT[guide] || GUIDE_CONTENT['android-manual'];

    useEffect(() => {
        closeRef.current?.focus();
        const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-[140] flex items-end justify-center bg-black/45 px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 backdrop-blur-sm sm:items-center" role="presentation" onMouseDown={onClose}>
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="carfido-install-title"
                className="max-h-full w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 id="carfido-install-title" className="text-xl font-black text-[#414141]">{content.title}</h2>
                    <button ref={closeRef} type="button" aria-label="Close install instructions" onClick={onClose} className="rounded-full p-2 text-[#707070] hover:bg-[#F9F9F9]">✕</button>
                </div>
                <div className="mt-4">{content.body()}</div>
                <button type="button" onClick={onClose} className={`mt-4 h-11 w-full ${BTN_PRIMARY}`}>Got it</button>
            </section>
        </div>
    );
};

export function usePwaInstallState() {
    const [state, setState] = useState(() => getPwaInstallState());
    useEffect(() => subscribeToPwaInstallState(() => setState(getPwaInstallState())), []);
    return state;
}

export const PwaInstallPrompt = () => {
    const state = usePwaInstallState();
    const guide = getPwaInstallGuide(state);
    const [showInstructions, setShowInstructions] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleInstall = useCallback(async () => {
        if (!state.canPrompt) {
            setShowInstructions(true);
            return;
        }
        setBusy(true);
        try { await requestPwaInstall(); } finally { setBusy(false); }
    }, [state.canPrompt]);

    const closeInstructions = useCallback(() => {
        setShowInstructions(false);
        dismissPwaInstallPrompt();
    }, []);

    if (!state.mobile || !state.engaged || state.dismissed || state.installed) return null;
    if (!state.ios && !state.canPrompt) return null;

    return (
        <>
            <aside className="fixed bottom-[calc(68px+env(safe-area-inset-bottom))] left-3 right-3 z-[70] mx-auto max-w-md rounded-3xl border border-[#d8d8d8] bg-white p-4 shadow-[0_18px_60px_rgba(20,20,20,0.25)] md:bottom-5" aria-label="Install CarFido">
                <button type="button" aria-label="Dismiss Add to Home Screen prompt" onClick={() => dismissPwaInstallPrompt()} className="absolute right-3 top-3 rounded-full p-1.5 text-[#7f7f7f] hover:bg-slate-100">✕</button>
                <div className="flex items-center gap-3 pr-8">
                    <img src="/android-chrome-192x192.png" alt="" className="h-12 w-12 rounded-xl shadow-sm" />
                    <div>
                        <p className="text-base font-black text-[#414141]">Add CarFido to your home screen</p>
                        <p className="mt-0.5 text-xs font-semibold text-[#414141]">Jump back into your garage and chats like an app.</p>
                    </div>
                </div>
                <button type="button" disabled={busy} onClick={handleInstall} className="mt-3 h-11 w-full rounded-xl bg-[#D81F26] text-sm font-black text-white hover:bg-[#B8181E] disabled:opacity-60">
                    {busy ? 'Opening install prompt...' : state.ios ? 'See iPhone steps' : 'Add to Home Screen'}
                </button>
            </aside>
            {showInstructions && <InstallDialog guide={guide} onClose={closeInstructions} />}
        </>
    );
};

export const INSTALL_CONFIRM_TIMEOUT_MS = 60 * 1000;

const CARD_COPY = {
    standalone: "You're using the CarFido app.",
    installed: 'CarFido is installed on this device. Open it from your home screen or app list.',
    installing: 'Installing CarFido… It will appear on your home screen when your browser finishes.',
    mobile: 'Open CarFido from your home screen in one tap, full screen without the browser bar.',
    'desktop-prompt': 'Install CarFido on this computer, or open www.carfido.com on your phone to add it to your home screen.',
    desktop: 'To add CarFido to your phone, open www.carfido.com in Safari on iPhone or Chrome on Android, then come back to Settings.',
};

export const AddToHomeScreenCard = () => {
    const state = usePwaInstallState();
    const guide = getPwaInstallGuide(state);
    const [showInstructions, setShowInstructions] = useState(false);
    const [busy, setBusy] = useState(false);
    // Set once the user accepts the native prompt; cleared when `appinstalled` confirms.
    const [installing, setInstalling] = useState(false);
    const [notice, setNotice] = useState('');

    useEffect(() => {
        if (!installing) return undefined;
        if (state.installed) {
            setInstalling(false);
            setNotice('CarFido was added. Open it from your home screen.');
            return undefined;
        }
        // No `appinstalled` within a minute: don't leave the button stuck or claim success.
        const timer = setTimeout(() => {
            setInstalling(false);
            setNotice("If CarFido doesn't appear on your home screen, try installing again.");
        }, INSTALL_CONFIRM_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [state.installed, installing]);

    const closeInstructions = useCallback(() => setShowInstructions(false), []);

    const handleInstall = async () => {
        setNotice('');
        if (guide !== 'android-prompt' && guide !== 'desktop-prompt') {
            setShowInstructions(true);
            return;
        }
        setBusy(true);
        try {
            const result = await requestPwaInstall();
            if (result.outcome === 'accepted') setInstalling(true);
            else if (result.outcome === 'dismissed') setNotice('Installation cancelled. You can install CarFido from here any time.');
            else if (result.outcome === 'unavailable') setShowInstructions(true);
        } finally {
            setBusy(false);
        }
    };

    let copyKey = state.mobile ? 'mobile' : guide;
    if (state.standalone) copyKey = 'standalone';
    else if (state.installed) copyKey = 'installed';
    else if (installing) copyKey = 'installing';

    const showButton = !state.installed && guide !== 'desktop';
    const buttonLabel = state.ios ? 'Add CarFido to Home Screen' : 'Install CarFido';

    return (
        <>
            <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-4">
                    <img src="/android-chrome-192x192.png" alt="" className="h-12 w-12 rounded-xl shadow-sm" />
                    <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-black text-[#414141]">Add to Home Screen</h2>
                        <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">
                            {state.installed
                                ? 'CarFido is installed on this device.'
                                : state.mobile
                                    ? 'Add the CarFido icon to your phone for quick, full-screen access.'
                                    : 'Open CarFido in your phone browser to add it to your home screen.'}
                        </p>
                        {notice && <p className="mt-2 text-xs font-bold text-[#414141]">{notice}</p>}
                    </div>
                    {state.installed ? (
                        <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">Installed</span>
                    ) : state.mobile ? (
                        <button type="button" disabled={busy} onClick={handleInstall} className="shrink-0 rounded-xl bg-[#D81F26] px-4 py-2.5 text-sm font-black text-white hover:bg-[#B8181E] disabled:opacity-60">
                            {busy ? 'Opening...' : 'Add'}
                        </button>
                    ) : null}
                </div>
            </section>
            {showInstructions && <InstallDialog guide={guide} onClose={closeInstructions} />}
        </>
    );
};
